// 游戏状态仓库（EP-002-S6 / S7）—— Zustand 单例。
//
// 设计要点：
// - `save` 是唯一真相；`phase` 由 save 推导（不冗余存）。
// - setSave 类操作集中于此，UI 只通过 selector + action 改状态（不直接 setSave）。
// - `lastSettledTripId` 的写入**只**发生在 src/domain/settleTrip.ts（CI 守门 check-settle-ownership）；
//   本仓库的 settle 动作委托给它，绝不自行赋值该字段。
// - 计时严格走真实时间差（computeElapsed），store 不累加任何 elapsed（CI 守门 check-no-tick-accumulator）。
import { create } from 'zustand';
import type { SaveDataV1, DestinationId, TripV1, PostcardEntry } from '../save/types';
import { CURRENT_SCHEMA_VERSION } from '../save/constants';
import { loadSave } from '../save';
import { constructStorageAdapter } from '../save/storage';
import { now } from '../core/time';
import { computeElapsed } from '../core/timer';
import { randomRng } from '../core/rng';
import { newTripId, newId } from '../core/id';
import { dropConfig } from '../domain/drop/dropConfig';
import { planReturn } from '../domain/drop/rollOutcome';
import { pureSettle } from '../domain/settleTrip';
import { rollPostcard } from '../domain/travel/postcardRoll';
import { POSTCARD_NOTE_BY_ID } from '../runtime/postcardNotes';
import { addToInventory as addToInventoryDomain } from '../domain/inventory';
import {
  revealSnapshot,
  noteDedupeKey,
  EMPTY_HANDS_DEDUPE_KEY,
  type RevealSnapshot,
} from '../runtime/sequencer';
import { setAdapter, persistNow, subscribeToStore } from './persist';
import { track } from '../telemetry';
// ★ EP-010-S1：云端身份 / 同步状态的异步探活钩子（init() 后 fire-and-forget 触发）
//   §v3-J 真相源原则：本导入只读 /health / /me，**不**写 save、不碰 persist.ts 节流链
// ★ EP-010-S3 基础同步接入（任务 P13-SYNC-WIRE）：
//   `getSave / putSave` —— GET/PUT /save 在启动拉取 + 变更上传两处使用。
//   严格不越界：S5 restore / S6 decideSyncAction / S7 断网占位都不在本工单。
import { getHealth, getMe, getSave, putSave } from '../api/client';
import { refresh } from '../api/auth';
import { ApiError } from '../api/types';

// 🆕 STEP 5-B：明信片在 tick 内 t=0.5 首次跨越时一次性 roll（仅一次）。
//   这里抽到的概率 0.55、缺席 0.45 见 rollPostcard 的 POSTCARD_SEND_PROBABILITY。
//   阈值 0.5 与模糊档位 2 ([0.15, 0.40)) 紧邻，玩家感知"刚走出家门"就有明信片寄出。
export const POSTCARD_TRIGGER_PROGRESS = 0.5;

export type Phase = 'no-save' | 'home' | 'away' | 'awaitingReveal';
export type StorageState =
  | 'uninitialized'
  | 'ok'
  | 'empty'
  | 'corrupt'
  | 'futureVersion'
  | 'storageUnavailable';

// 视图层 UI 态（非存档、不落盘，刷新即复位）：home=主场景 / reveal=揭晓演出 / space=空间背包。
export type View = 'home' | 'reveal' | 'space';

// 🆕 v2：空间背包内 tab（不落盘，刷新即复位）。StatusCard 角标点击入空间时设 'postcards'。
export type SpaceTab = 'gifts' | 'postcards';

// ★ EP-010-S1：云端身份 / 同步状态
//   authState:  是否已登录（S4 接 Cognito 后才有 'authenticated'）
//   syncState:   /health 探活结果（'idle' / 'syncing' / 'ok' / 'error'）
//   这两个字段是 §v3-J 真相源原则下"云端状态在客户端的镜像"，**不**参与 dogState / trip 判定
//   （§v3-J.2 硬纪律）。S7 起 AuthGuard 用它们做路由级锁（offline_blocked / login_required），
//   但判定逻辑路径本身仍走 §v3-J.2 云端返回值 + Date.now()，不读 localStorage
export type AuthState = 'unknown' | 'login_required' | 'authenticated';
export type SyncState = 'idle' | 'syncing' | 'ok' | 'error';

export interface GameStore {
  save: SaveDataV1 | null;
  storageState: StorageState;
  clock: number; // 仅供模糊文案触发重渲染，不承载任何时间/进度语义
  view: View; // UI 视图态（home / reveal / space）
  revealSnapshot: RevealSnapshot | null; // 揭晓演出只读快照（先入库后构造）
  dogLeaving: boolean; // 离场演出中（纯 UI 态，不落盘；动画结束即复位）
  spaceTab: SpaceTab; // 🆕 v2：空间 tab（home / space 切换时复位默认 'gifts'，从角标入 'postcards'）

  // ★ EP-010-S1：云端身份 / 同步状态（init 后由 runSyncBootstrap 异步探测填充）
  //   §v3-J 真相源：这两字段**不**参与 dogState / trip / inventory 判定（§v3-J.2）
  //   仅给 AuthGuard（S7）/ SyncBanner（S7）/ AuthPanel（S4）做状态显示用
  authState: AuthState;
  syncState: SyncState;

  // ★ EP-010-S3（基础同步接入，P13-SYNC-WIRE 任务）：
  //   云端当前档的 rev（CAS 单调字段），§v3-D.1 #4 纪律产物。
  //   - `null` = 启动初始 / 尚未从云端拉过（不要用本地推导）
  //   - `0`    = 已确认云端无档（GET 404 后；视为 baseRev=0 起点，
  //              §v3-D.1 #5 迁移别名）
  //   - `>0`   = 云端当前 rev（GET 200 后 / PUT 200 后由响应 rev 被动回写）
  //   维护纪律：
  //   - **永远** 取云端返回的 rev，绝不本地自增（避免领先于云端）
  //   - PUT 时 `baseRev = cloudRev ?? 0`（S6 decideSyncAction 接管全决策）
  //   - 本字段**不参与**狗状态 / trip / inventory 判定（§v3-J.2 硬纪律）
  cloudRev: number | null;

  init: () => void;

  // ★ EP-010-S3 基础同步接入（P13-SYNC-WIRE）：
  //   把云端权威值覆写到本地 store —— **唯一**写本地 save 的同步入口。
  //   实现统一走 `set({save, cloudRev})`，由 persist.ts subscribe 链**被动**写盘
  //   （符合 §v3-J.2 "GET 成功后被动写入" 的语义）。
  //   本函数**不**直接调用 localStorage，也不**不**读 localStorage 判定
  //   （§v3-J.1 第 2 条）。
  applyCloudSave: (cloudSave: SaveDataV1, rev: number) => void;

  // ★ EP-010-S3 基础同步接入：触发一次 `PUT /save`，按状态分支：
  //   - 200 → 更新 `cloudRev`
  //   - 401 → `markLoginRequired()`（复用 S4 的 authState 联动；§v3-C.5）
  //   - 409 → **简化处理**：取云端覆盖本地 + console.warn 标记 S6 接管
  //     （不在本工单实现 §v3-D.5 兜底幂等 / 快照 / restore —— S6 decideSyncAction 接管）
  //   - 5xx / network → 静默（§v3-H.4 指数退避由 S7 接入；本工单不越界）
  uploadToCloud: () => Promise<void>;

  depart: (travelDurationMs: number, destinationId: DestinationId, isLongTrip?: boolean) => void;
  endDogLeave: () => void; // 离场动画结束回调（onAnimationEnd / 兜底计时器都会调）
  tick: (t: number) => void;
  markRevealReady: () => void;
  settle: () => void;
  addToInventory: (giftId: string, count?: number) => void;
  beginReveal: () => void; // 先快照 → 结算落盘 → 进 reveal 演出（EP-007-S4）
  endReveal: () => void; // 演出结束 → 进 space
  openSpace: (initialTab?: SpaceTab) => void; // 进入空间（home/away/awaitingReveal 皆可，老板 2026-09-15 拍板；v2 新增可选入参）
  closeSpace: () => void; // 从空间返回 home
  // 🆕 STEP 5-B：详情页打开 → 标记某条明信片已读（红点 -1）。
  markPostcardRead: (entryId: string) => void;
  // 🆕 v2.1：礼物详情打开 → 标记某 stack 已读（礼物 tab 角标 -1；TopBar 空间按钮合计未读 -1）。
  markGiftRead: (giftId: string) => void;
}

// 由 save 推导 phase（唯一真相在 save，不冗余存）。
export function selectPhase(s: Pick<GameStore, 'save'>): Phase {
  if (s.save === null) return 'no-save';
  // dogState 取值恰为 home / away / awaitingReveal
  return s.save.dogState;
}

// 回写留言去重键（GDD 02 §②-2.7「同一目的地连续两次不重复」）。
// 两条结算路径（beginReveal / settle）**共用本函数**，保证「选」与「记」同源、规则不可能漂移：
// 键由 runtime/sequencer 的 noteDedupeKey 推导（v2：移除明信片 'postcard' 键 → 仅 'longtrip' / dest）。
// 🆕 P6-POLISH-ENG-002 §B.5：空手归来时留言走空手池（EMPTY_HANDS_NOTES），其去重键是
//   `EMPTY_HANDS_DEDUPE_KEY`（与 'longtrip' / dest 同级），必须按"是否空手"分写，
//   否则空手留言的"连续不重复"判定永远读不到上次那一条（每次都当全新）。
// trip 为空（未真正结算）时原样返回，绝不写键。
function withNoteDedupe(next: SaveDataV1, noteId: string, trip: TripV1 | null): SaveDataV1 {
  if (!trip) return next;
  // 空手判定：有 dropResult 且 giftIds 为空 ⇔ planReturn 判定的空手（roll.ts 红线保证正常掉落恒 ≥1）。
  const emptyHanded = trip.dropResult !== null && trip.dropResult.giftIds.length === 0;
  const key = emptyHanded
    ? EMPTY_HANDS_DEDUPE_KEY
    : noteDedupeKey(trip.isLongTrip, trip.destinationId);
  return {
    ...next,
    lastNoteIdByDestination: { ...next.lastNoteIdByDestination, [key]: noteId },
  };
}

function createFreshSave(): SaveDataV1 {
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    dogState: 'home',
    trip: null,
    lastSettledTripId: null,
    stats: { tripCount: 0, isFirstTripDone: false },
    lastNoteIdByDestination: {},
    settings: { timeScale: 1, duskTintEnabled: false },
    inventory: [],
    savedAt: now(),
    // 🆕 v2：mailbox + lastPostcardNoteIdByDestination 必填字段。
    mailbox: [],
    lastPostcardNoteIdByDestination: {},
  };
}

let subscribed = false;

// ───────────────────────────────────────────────────────────────────────────
// ★ EP-010-S4：authState 联动（**不新增字段** —— 只用 S1 已有的 authState 三态）
//
//   登录成功 → 'authenticated'；云端 401 → 'login_required'；断网 / 5xx → **保持**。
//
//   §v3-J 真相源原则：authState 是"云端返回值在客户端的镜像"，**不**是本地判定结果。
//   本文件任何路径都不读 localStorage 做登录判定（S7 起由 AuthGuard 消费本字段做路由级锁）。
// ───────────────────────────────────────────────────────────────────────────

/** 登录成功（云端 /auth/login 或 /auth/refresh 返回 200）→ 置为已登录。 */
export function markAuthenticated(): void {
  useGameStore.setState({ authState: 'authenticated' });
  // 登录成功 / refresh 恢复 / 启动时已登录 → 立即拉云端权威存档（§v3-J 真相源），
  // 设 cloudRev。否则 scheduleCloudSync 的 PUT 会一直用 baseRev=0 → 409 风暴。
  // tryCloudBootstrap 内部对「未登录」会静默 return，这里已确保是 authenticated。
  void tryCloudBootstrap();
}

/** 云端明确回 401（无 token / token 失效）→ 置为需要登录。 */
export function markLoginRequired(): void {
  useGameStore.setState({ authState: 'login_required' });
}

/**
 * 向云端问一次"我是谁"（S4 bootstrap，§v3-C.5）：
 *   ① POST /auth/refresh —— 用 httpOnly refresh cookie 换 access token 进内存
 *      （首次访问没有 cookie，401 是**正常路径**，不是异常，静默吞掉）
 *   ② GET /auth/me —— 云端权威判定登录态
 *   ③ 401 → 'login_required'；断网 / 5xx → **保持原状态**（§v3-H 断网态由 S7 接管，
 *      本工单不越界：不把"网络不通"误判成"必须登录"，也不误放行）
 *
 * ★ 本函数**不**读 localStorage —— 判定 100% 来自云端返回值（§v3-J.2 硬纪律）。
 */
export async function probeAuthState(): Promise<void> {
  try {
    await refresh();
  } catch {
    // 无 refresh cookie 或已过期：正常，继续用现有（可能为空的）token 问 /auth/me
  }
  try {
    const me = await getMe();
    if (typeof me.userId === 'string' && me.userId.length > 0) {
      markAuthenticated();
      return;
    }
    markLoginRequired();
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      markLoginRequired();
    }
    // 其它失败（status 0 断网 / 5xx）→ 保持 unknown，交给 S7 的离线占位处理
  }
}

/**
 * ★ EP-010-S1：init() 后挂的异步 sync 启动钩子（fire-and-forget，不阻塞 init）
 *
 * 行为（S1 范围 — 严格不越界）：
 *   - 探测 /health → 设 syncState 字段（'syncing' → 'ok' | 'error'）
 *   - 探测 /me    → 设 authState 字段（'unknown' → 'authenticated' | 'login_required' | 'unknown'）
 *   - **不**调 GET /save（S3 的事）/ **不**写 save / **不**碰 persist.ts 节流链
 *
 * 失败处理（S1 阶段）：
 *   - 一次失败即记 error / login_required；S7 才上指数退避（§v3-H.4）
 *   - 失败**绝不**抛到 init()（init 已被 EP-002-S7 实现完，本钩子只追加）
 *
 * §v3-J 真相源原则（硬纪律）：
 *   - 本钩子**只**写 authState / syncState 字段；**不**写 save、**不**读 save、**不**读 localStorage
 *   - 任何 dogState / trip / inventory 判定路径都不经过本钩子（§v3-J.2）
 *   - localStorage 缓存写入由 src/store/persist.ts 唯一把控，与本钩子互不干扰
 *
 * 触发点：init() 末尾（subscribeToStore 之后），保证本地 store 已 setState、订阅已建立
 */
async function runSyncBootstrap(): Promise<void> {
  // ─────────── /health 探活 ───────────
  useGameStore.setState({ syncState: 'syncing' });
  let syncOk = false;
  try {
    const health = await getHealth();
    if (health && health.ok === true) {
      syncOk = true;
    }
  } catch {
    // §v3-H.4：S7 才有指数退避（1s/2s/4s + 30s 心跳）；S1 单次失败即 error
    syncOk = false;
  }
  useGameStore.setState({ syncState: syncOk ? 'ok' : 'error' });

// ─────────── 登录态探测（S4 起统一走 probeAuthState：先 refresh cookie → 再 /auth/me）───────────
//   S1 的判定逻辑（401 → login_required / 断网 → 保持 'unknown'）**语义不变**，
//   只是把"先拿 token 再问云端"这一步补上（§v3-C.5 bootstrap），判定仍 100% 走云端。
  await probeAuthState();

  // ─────────── EP-010-S3 基础同步接入（P13-SYNC-WIRE）：
  //   已登录 → 从云端拉权威值并决定下一步（覆盖本地 / 首次上传 / 静默）。
  //   未登录 → 直接返回（拉云端需 token，无 token 时 §v3-C.5 由 S7 占位）。
  await tryCloudBootstrap();
}

/**
 * ★ EP-010-S3 基础同步接入（P13-SYNC-WIRE）：启动时拉云端权威值，按状态分支落本地 / 上传本地。
 *
 * 行为：
 *   - `authState !== 'authenticated'` → 直接返回（静默，不抛错；§v3-C.5 兜底）
 *   - GET /save 200 → 云端权威 → `applyCloudSave(parsed, resp.rev)` 覆盖本地
 *     （§v3-J 真相源原则；由 persist.ts subscribe 链被动写盘）
 *   - GET /save 404 → 云端无档 → `cloudRev = 0`；若本地有档 → 首次 `PUT /save`
 *     （`baseRev = 0, intent = 'migrate'`；§v3-D.1 #5 迁移别名）
 *   - GET 5xx / network → 静默（§v3-H.4 指数退避由 S7 接入；本工单不越界）—— **不**误判 login_required
 *   - GET 401 → `markLoginRequired()`（与 §v3-C.5 authState 联动一致）
 *   - migrate PUT 失败（200 之外）→ 静默（下次变更触发上传；不让启动卡死）
 *
 * §v3-J.1 硬纪律：本函数**不读不写** localStorage（仅经 `setState` 触发 subscribe 链被动物理写盘）。
 */
async function tryCloudBootstrap(): Promise<void> {
  // 不在未登录时拉云端（拿到 401 是预期的，留给 S7 统一处理）
  if (useGameStore.getState().authState !== 'authenticated') return;

  const localSave = useGameStore.getState().save;
  try {
    const resp = await getSave();
    if ('data' in resp) {
      // 200：云端权威 → 覆盖本地（§v3-J 真相源）
      const parsed = JSON.parse(resp.data) as SaveDataV1;
      useGameStore.getState().applyCloudSave(parsed, resp.rev);
      return;
    }
    // 404：云端无档 → 把 cloudRev 落 0（首次上传的 baseRev 起点，§v3-D.1 #5 迁移别名）
    useGameStore.setState({ cloudRev: 0 });
    if (localSave) {
      try {
        const ok = await putSave({
          baseRev: 0,
          data: JSON.stringify(localSave),
          intent: 'migrate',
        });
        useGameStore.setState({ cloudRev: ok.rev });
      } catch (e) {
        // 启动首次上传失败：401 → 登录失效；其它（断网/5xx）→ 静默，等下次变更再试
        if (e instanceof ApiError && e.status === 401) {
          markLoginRequired();
        }
      }
    }
  } catch (e) {
    // GET 失败：401 → 登录失效；其它 → 静默（保持现状，不误判 —— §v3-J.2 + §v3-H 边界）
    if (e instanceof ApiError && e.status === 401) {
      markLoginRequired();
    }
  }
}

// ★ EP-010-S3（P13-SYNC-WIRE）：导出 `tryCloudBootstrap` 仅供单元测试入口。
//   runSyncBootstrap 已包装其调用；产品代码不应直接调它。
//   这条 export 的存在让单测能在不触发完整 init() 链路的情况下验证「启动拉云端」分支。
/** @internal */
export const _test_tryCloudBootstrap = tryCloudBootstrap;

export const useGameStore = create<GameStore>()((set, get) => ({
  save: null,
  storageState: 'uninitialized',
  clock: 0,
  view: 'home',
  revealSnapshot: null,
  dogLeaving: false,
  spaceTab: 'gifts', // 🆕 v2：空间 tab 默认 'gifts'

  // ★ EP-010-S1：云端身份 / 同步状态初值（init 后由 runSyncBootstrap 异步覆盖）
  authState: 'unknown', // ★ 尚未探活（S1：syncBootstrap 完成后变 'login_required' | 'authenticated'）
  syncState: 'idle',    // ★ 尚未探活（S1：syncBootstrap 完成后变 'ok' | 'error'）

  // ★ EP-010-S3 基础同步接入（P13-SYNC-WIRE）：
  //   启动时未与云端对话 → null；runSyncBootstrap -> tryCloudBootstrap 完成后
  //   落 0（GET 404）或云端当前 rev（GET 200）。
  //   本地没有此字段前的旧存档不会因此字段丢失 —— §v3-J.1 不读 localStorage 判定纪律保留。
  cloudRev: null,

  init: () => {
    const adapter = constructStorageAdapter();
    setAdapter(adapter);
    const result = loadSave(adapter);

    let storageState: StorageState = 'ok';
    let save: SaveDataV1 | null = null;

    switch (result.kind) {
      case 'ok':
        save = result.save;
        break;
      case 'empty':
        save = createFreshSave();
        persistNow(save); // 首次启动写一份新档
        storageState = 'empty';
        break;
      case 'corrupt':
        // T9：备份已在 loadSave 内完成，这里重置为新档（保留提示）
        save = createFreshSave();
        persistNow(save);
        storageState = 'corrupt';
        break;
      case 'futureVersion':
        // T8：只读、绝不写回、不重置（提示用户「版本太新」）
        save = null;
        storageState = 'futureVersion';
        break;
      case 'storageUnavailable':
        // T10：降级内存态，游戏可玩，提示「无法保存」
        save = createFreshSave();
        storageState = 'storageUnavailable';
        break;
    }

    set({ save, storageState, clock: get().clock + 1 });

    if (!subscribed) {
      subscribeToStore();
      subscribed = true;
    }

    // ★ EP-010-S1：异步 sync 启动钩子（不替换、不改节流逻辑，仅追加）
    //   fire-and-forget：失败由钩子内部捕获，绝不影响 init 后续路径
    //   真相源：钩子只写 authState/syncState，不读不写 save、不碰 persist.ts
    void runSyncBootstrap();
  },

  // ★ EP-010-S3 基础同步接入（P13-SYNC-WIRE）— 见接口注释对 §v3-J.2 纪律的展开。
  //   实现要点：
  //   1) 用 `set({save, cloudRev})` 触发 persist.ts subscribe 链被动物理写盘 —— 这是
  //      §v3-J.2 "GET 成功后被动写入" 纪律的物理实现，不另开 localStorage 路径。
  //   2) `clock + 1` 触发订阅者重渲染（与本文件其他 action 行为一致）。
  //   3) 不做 schemaVersion / 字段完整性校验 —— §v3-J 信任云端返回值
  //      （server 端已在写入前完成 migrate / sanitize）。
  applyCloudSave: (cloudSave, rev) => {
    set({ save: cloudSave, cloudRev: rev, clock: get().clock + 1 });
  },

  uploadToCloud: async () => {
    const state = get();
    const save = state.save;
    if (!save) return; // 本地无档无可同步内容（理论上云端也是 404，留给下次启动 bootstrap 处理）

    // §v3-D.1 #4：baseRev 永远取云端回过的 cloudRev。S6 decideSyncAction 接管全决策。
    // cloudRev=null 表示 tryCloudBootstrap 还在跑 / 失败了 —— 等下次 scheduleCloudSync 再试
    // （不放行 baseRev=0 PUT 是为了避免与云端不同步时产生 409 风暴）
    if (state.cloudRev === null) {
      console.warn('[P13 sync] cloudRev 还未就绪，跳过本轮上传（云端还没拉到）');
      return;
    }
    const baseRev = state.cloudRev;
    try {
      const ok = await putSave({
        baseRev,
        data: JSON.stringify(save),
        intent: 'normal',
      });
      // §v3-J.2 被动写回 —— 用云端返回的 rev 覆盖本地 cloudRev，绝不本地自增。
      set({ cloudRev: ok.rev });
    } catch (e) {
      if (!(e instanceof ApiError)) throw e; // 非 ApiError 直接抛出（少见，但保险）
      if (e.status === 401) {
        // §v3-C.5：401 → 等 refresh 失败 / 已无效；这里直接转 login_required
        markLoginRequired();
        return;
      }
      if (e.status === 409) {
        // ★ EP-010-S3 简化冲突（S6 decideSyncAction 接管全决策）：
        //   兜底"取云端 → 覆盖本地"（§v3-J 真相源原则） —— 不做快照、不做 restore、
        //   不做内容 hash 比对（§D.5 兜底幂等留给 S6）；console.warn 标注 S6 接入点。
        console.warn(
          '[sync] PUT /save rev conflict — simplified to "take cloud"; ' +
          'S6 decideSyncAction will replace this branch (see src/store/index.ts runSyncBootstrap / P13-SYNC-WIRE).'
        );
        try {
          const resp = await getSave();
          if ('data' in resp) {
            const parsed = JSON.parse(resp.data) as SaveDataV1;
            get().applyCloudSave(parsed, resp.rev);
          }
        } catch {
          // 409 后再拉仍失败 → 静默（S7 断网占位接管；本工单不越界）
        }
        return;
      }
      // 5xx / network / status 0 → 静默（§v3-H.4 指数退避由 S7 接入；本工单不越界）
    }
  },

  depart: (travelDurationMs, destinationId, isLongTrip = false) => {
    const save = get().save;
    // T6：非 home 态不可出发
    if (!save || save.dogState !== 'home') return;

    const trip: TripV1 = {
      id: newTripId(),
      departureTimestamp: now(),
      travelDurationMs,
      destinationId,
      isLongTrip,
      status: 'inProgress',
      dropResult: null,
      noteId: '', // 留言在 EP-007 接入；§13-A 已预留字段
    };

    set({ save: { ...save, dogState: 'away', trip }, dogLeaving: true, clock: get().clock + 1 });
    // 🆕 EP-009-S5：埋点 trip_started —— 字段逐字取主架构 §11 / 任务规格
    track('trip_started', {
      destinationId,
      durationMs: travelDurationMs,
      tripCount: save.stats.tripCount,
    });
    // 兜底计时器：动画 end 事件被节流/吞掉时也能复位（1600ms = 动画 1.5s + 缓冲）
    window.setTimeout(() => {
      if (get().dogLeaving) set({ dogLeaving: false });
    }, 1600);
  },

  endDogLeave: () => {
    if (get().dogLeaving) set({ dogLeaving: false, clock: get().clock + 1 });
  },

  tick: (t: number) => {
    const save = get().save;
    if (!save || save.dogState !== 'away' || !save.trip) {
      // 非在途：仍 bump clock 让 UI 在 home/awaitingReveal 下保持一致（无副作用）
      set((s) => ({ clock: s.clock + 1 }));
      return;
    }

    const { progress, ready } = computeElapsed(
      save.trip.departureTimestamp,
      t,
      save.trip.travelDurationMs,
    );

    let nextSave = save;

    // 🆕 STEP 5-B：明信片在 t=0.5（progress >= POSTCARD_TRIGGER_PROGRESS）首次跨越时
    //   一次性 roll。判定用 mailbox 中是否已有 tripId 关联的 entry —— 有了就不再抽
    //   （无论后续 tick 多少次 progress 始终 >= 0.5，只触发一次）。
    //   离线 8h 后第一次 tick：progress=8 (clamped to 1)，有 mailbox[tripId] ? 否 → roll 一次。
    const hasRolledForTrip = save.mailbox.some((p) => p.tripId === save.trip!.id);
    if (!hasRolledForTrip && progress >= POSTCARD_TRIGGER_PROGRESS) {
      const postcardRoll = rollPostcard(
        randomRng(),
        {
          destinationId: save.trip.destinationId,
          // 🆕 P6-POLISH-ENG-002 §E.4：去重集合**由 mailbox 派生**，不再读 lastPostcardNoteIdByDestination。
          //   这样"我记得你收到过哪些"永远等于"你实际收到过哪些"，两者不可能漂移。
          receivedNoteIds: new Set(save.mailbox.map((e) => e.noteId)),
          // 用当前 tripCount（结算时才是 tripCount+1；这里路上寄，与 tripCount 平齐）
          tripCount: save.stats.tripCount,
        },
        t,
      );
      if (postcardRoll.sent && postcardRoll.note) {
        const def = POSTCARD_NOTE_BY_ID[postcardRoll.note.id];
        if (def) {
          const entry: PostcardEntry = {
            id: newId(),
            tripId: save.trip.id,
            destinationId: save.trip.destinationId,
            noteId: def.id,
            stampedAt: postcardRoll.rolledAt,
            receivedAt: t,
            imageAsset: null,
            isRead: false, // 🆕 STEP 5-B：路上寄出时为未读，玩家打开详情后才转已读
          };
          nextSave = {
            ...nextSave,
            mailbox: [...nextSave.mailbox, entry],
            // ⚠️ P6-POLISH-ENG-002 §E.4：`lastPostcardNoteIdByDestination` **停止写入**
            //   （字段保留不删，避免 migrate / sanitize 的改动面；去重已改由 mailbox 派生）。
            //   继续写会让它再次变成第二真相源，正是本次要消除的漂移来源。
          };
        }
      } else {
        // 缺席：不入 mailbox，只推进 dedupe？不推进——缺席不写键，避免污染去重。
        // 这一行是"明信片缺席 = 啥都不发生"的语义注释。
      }
    }

    if (ready) {
      // ②-2.3a：先生成掉落并落盘，再改 awaitingReveal（顺序不可颠倒，避免「已归来却无礼物」）
      //
      // 🆕 P6-POLISH-ENG-002 §B.3：把 roll() 换成 planReturn() 包装器。
      //   roll.ts 是 GDD 01 红线的载体（giftIds 恒 ≥ 1），**一行未改**；
      //   空手与否是**归来策略**，由 rollOutcome.planReturn 在上层决定。
      //   ⚠️ rng 消耗比原先 **+1 次**（空手判定），仅在 ready 时消耗 —— 脚本化 rng 测试需留意。
      const outcome = planReturn({
        config: dropConfig,
        destination: save.trip.destinationId,
        tripCount: save.stats.tripCount,
        isFirstTrip: !save.stats.isFirstTripDone, // 首旅永不空手
        lastReturnWasEmpty: save.lastReturnWasEmpty === true, // 连续两次空手禁止
        // 🆕 GDD 01 §9：反重复保护窗口（按趟分组；缺省/空窗口机制静默通过）。
        recentTripDrops: save.recentTripDrops ?? [],
        rng: randomRng(),
        now: t,
      });
      const trip: TripV1 = { ...save.trip, status: 'awaitingReveal', dropResult: outcome.dropResult };
      nextSave = { ...nextSave, dogState: 'awaitingReveal', trip };
    }

    set({ save: nextSave, clock: get().clock + 1 });
  },

  // 注：本路径同样需写 `lastNoteIdByDestination` 去重键（与 beginReveal 同源，勿删）——
  // 它最终也走 settle()，写入在 settle 内统一完成。
  markRevealReady: () => {
    const save = get().save;
    if (!save || save.dogState !== 'awaitingReveal' || !save.trip) return;
    const dropResult = save.trip.dropResult;
    if (!dropResult) return; // 异常：awaitingReveal 必有 dropResult（C4 顺序约束保证）
    get().settle();
  },

  // 注：本路径同样需写 `lastNoteIdByDestination` 去重键（与 beginReveal 同源，勿删）——
  // 见下方 withNoteDedupe 调用；当前 UI 走 beginReveal，但本 action 仍可被调用，行为必须一致。
  // 🆕 STEP 5-B：明信片不再由 settle 触发 —— 改为 tick 在 t=0.5 触发（详见 tick 注释）。
  settle: () => {
    const save = get().save;
    if (!save) return;
    // 委托 domain/settleTrip（lastSettledTripId 唯一写入者）；先入库后关闸门在内部完成
    const next = pureSettle(save, { now: now() });
    if (next === save) {
      // 守卫命中（trip 为空 / 非 awaitingReveal / 已结算）：未真正结算，不写去重键
      set({ save: next, clock: get().clock + 1 });
      return;
    }
    // 真正结算：与 beginReveal 同源写入去重键（留言选取同样走 revealSnapshot，规则一致）
    const withNote = withNoteDedupe(next, revealSnapshot(save).noteId, save.trip);
    set({ save: withNote, clock: get().clock + 1 });

    // 🆕 EP-009-S5：埋点 returned + drop_rolled —— 仅在真正结算（pureSettle 真改了 save）时触发。
    //  任务规格：'在 pureSettle 调用后'。tripId / dropResult 从旧 save 读（pureSettle 已把 next.trip 置 null）。
    //  tripCount 用 withNote.stats.tripCount（pureSettle 内部 +1 后的新值）。
    const tripId = save.trip?.id;
    if (tripId !== undefined) {
      track('returned', { tripId, tripCount: withNote.stats.tripCount });
      const dropResult = save.trip?.dropResult;
      if (dropResult !== null && dropResult !== undefined) {
      track('drop_rolled', {
        tripId,
        giftIds: dropResult.giftIds,
        rolledAt: dropResult.rolledAt,
        // 🆕 GDD 01 §9.7：反重复降权命中列表（埋点字段，主架构 §11 同步）。
        decayedGiftIds: dropResult.decayedGiftIds,
      });
      }
    }
  },

  beginReveal: () => {
    const save = get().save;
    if (!save || save.dogState !== 'awaitingReveal' || !save.trip) return;
    // 1) 先构造演出快照（trip 仍存）；2) 再结算落盘（关闸门）；3) 进 reveal 演出。
    // 任何中断（切标签/刷新）时礼物已在 inventory，重新进入直接见结果（ADR-004 §2.5）。
    const snapshot = revealSnapshot(save);
    // 🆕 STEP 5-B：明信片在 tick 内 t=0.5 已寄出，settle 不再 roll。
    const next = pureSettle(save, { now: now() });

    // 回写留言去重键（GDD 02 §②-2.7「同一目的地连续两次不重复」）。
    // 此前该字段全仓只有读（sequencer）没有写，去重形同虚设——本次补上写入点。
    // 与 settle() 共用 withNoteDedupe（见下），两条结算路径同源。
    // 注意：与 check-settle-ownership 守门的 lastSettledTripId 无关，后者仍只在 domain/settleTrip.ts 写。
    // save.trip 用原始引用（pureSettle 已把 next.trip 置 null）。
    const withNote = withNoteDedupe(next, snapshot.noteId, save.trip);

    persistNow(withNote); // 写入时机：揭晓落盘（主架构 §6.4 步骤 D）

    // 🆕 EP-009-S5：埋点 drop_rolled + revealed —— 任务规格：「settle() 真正完成 / beginReveal 触发」。
    //  时序：pureSettle 内 upsertStacks 已跑（addToInventory 完成），persistNow 已写盘；set 进 reveal 演出前触发。
    //  tripId / dropResult 从旧 save.trip 读（pureSettle 已把 withNote.trip 置 null）。
    const tripId = save.trip.id;
    const dropResult = save.trip.dropResult;
    if (dropResult !== null && dropResult !== undefined) {
      track('drop_rolled', {
        tripId,
        giftIds: dropResult.giftIds,
        rolledAt: dropResult.rolledAt,
        // 🆕 GDD 01 §9.7：反重复降权命中列表（埋点字段，主架构 §11 同步）。
        decayedGiftIds: dropResult.decayedGiftIds,
      });
    }
    track('revealed', { tripId });

    set({ save: withNote, revealSnapshot: snapshot, view: 'reveal', clock: get().clock + 1 });
  },

  endReveal: () => {
    // 演出结束 → 进包裹（礼物已落盘，纯 UI 切换）。
    // spaceTab 显式归位 'gifts'：揭晓后玩家要看的就是刚带回来的礼物，
    // 不能被上一次浏览残留的 tab（如 'postcards'）顶掉（P6-POLISH-ENG-001）。
    set({ view: 'space', revealSnapshot: null, spaceTab: 'gifts', clock: get().clock + 1 });
  },

  openSpace: (initialTab?: SpaceTab) => {
    const save = get().save;
    if (!save) return;
    // 老板 2026-09-15 反馈离家也想看包裹：home/away/awaitingReveal 皆可进入。
    // 🆕 v2：可选入参 initialTab；不传时默认 'gifts'（从 StatusCard 角标入传 'postcards'）。
    //
    // ⚠️ 白名单兜底（P6-POLISH-ENG-001）：**不能用 `initialTab ?? 'gifts'`**。
    //   曾有调用点把本 action 直接当 onClick 传，React 的 MouseEvent 落进 initialTab ——
    //   它不是 null/undefined，`??` 判不出错，spaceTab 被写成 Event 对象，
    //   结果礼物/明信片两个 tab 都不命中 —— 包裹打开了却是一片空白，得手点「礼物」才有内容。
    //   这里显式只认联合字面量：非 'postcards' 一律落到 'gifts'，宁可偏默认也绝不进脏值。
    const tab: SpaceTab = initialTab === 'postcards' ? 'postcards' : 'gifts';
    set({ view: 'space', spaceTab: tab, clock: get().clock + 1 });
    // 🆕 EP-009-S5：埋点 view_changed —— 字段：{ to, tab }
    track('view_changed', { to: 'space', tab });
  },

  closeSpace: () => {
    set({ view: 'home', spaceTab: 'gifts', clock: get().clock + 1 });
    // 🆕 EP-009-S5：埋点 view_changed —— 字段：{ to: 'home' }
    track('view_changed', { to: 'home' });
  },

  addToInventory: (giftId, count = 1) => {
    const save = get().save;
    if (!save) return;
    const inventory = addToInventoryDomain(save.inventory, [{ giftId, count }], now());
    set({ save: { ...save, inventory }, clock: get().clock + 1 });
    // 🆕 EP-009-S5：埋点 inv_upserted —— 字段：{ giftId, count, tripCount }
    track('inv_upserted', { giftId, count, tripCount: save.stats.tripCount });
  },

  // 🆕 STEP 5-B 引入 / STEP 5-D 改调用点：
  //   - 5-B：由 PostcardDetail 的 mount useEffect 调（依赖 effect 调度，真浏览器不可靠）
  //   - 5-D：**改由 SpaceView 列表项点击处同步调**（先落盘 → 再 setOpenPostcardId 开详情），
  //     时序确定、不依赖 React effect 调度 / StrictMode 双挂载。
  //   行为：entry.isRead = true → 主页红点（mailbox.filter(!isRead).length）-1。
  //   幂等：重复点同一 entry 不重复触发（已是 true 就跳过，L338）。
  markPostcardRead: (entryId: string) => {
    const save = get().save;
    if (!save) return;
    const idx = save.mailbox.findIndex((p) => p.id === entryId);
    if (idx < 0) return; // 不存在
    if (save.mailbox[idx]!.isRead) return; // 已读 → 跳过（不重写、不 bump clock 无关状态）
    const nextMailbox = save.mailbox.map((p, i) =>
      i === idx ? { ...p, isRead: true } : p,
    );
    set({ save: { ...save, mailbox: nextMailbox }, clock: get().clock + 1 });
  },

  // 🆕 v2.1：礼物详情打开 → 标记该 giftId 的 stack 为已读。
  // 幂等：已是 true 跳过（不重写、不 bump clock）。
  markGiftRead: (giftId: string) => {
    const save = get().save;
    if (!save) return;
    const idx = save.inventory.findIndex((s) => s.giftId === giftId);
    if (idx < 0) return; // 不存在
    if (save.inventory[idx]!.isRead) return; // 已读 → 跳过
    const nextInventory = save.inventory.map((s, i) =>
      i === idx ? { ...s, isRead: true } : s,
    );
    set({ save: { ...save, inventory: nextInventory }, clock: get().clock + 1 });
  },
}));
